import { BaseLoadingStore } from "app/modules/common/stores/BaseLoadingStore";
import { action, makeObservable, observable, runInAction } from "mobx";
import { inject } from "react-ioc";
import { ApiClients } from "app/modules/company/services/AxiosBaseClient";
import { RateEntry, Region, RateGroup, RatePerMile, RegionDefinition } from "app/modules/company/api/clients.api";
import { FormState, FieldState } from "formstate";

export class RateSettingsStore extends BaseLoadingStore {
    @inject companyApiClients: ApiClients;

    title: string = 'Rate Settings';
    ratesFormState: FormState<any> = new FormState([]);
    allRegions: RegionDefinition[] = [];

    constructor() {
        super();

        makeObservable(this, {
            title: observable,
            ratesFormState: observable,
            allRegions: observable,
            init: action,
            saveRates: action,
            addGroup: action,
            removeGroup: action,
            addDestination: action,
            removeDestination: action,
            updateRate: action
        });
    }

    // Helper method to create a RatePerMile FormState
    private createRatePerMileFormState = (sprinter: number = 0, smallStraight: number = 0, largeStraight: number = 0) => {
        return new FormState({
            sprinter: new FieldState(sprinter),
            smallStraight: new FieldState(smallStraight),
            largeStraight: new FieldState(largeStraight)
        });
    };

    // Helper method to create a RateGroup FormState
    private createRateGroupFormState = (destinations: Region[] = [], rate?: any) => {
        return new FormState({
            destinations: new FieldState(destinations),
            rate: new FieldState(rate || this.createRatePerMileFormState())
        });
    };

    // Helper method to create a RateEntry FormState
    private createRateEntryFormState = (origin: Region, groups: any[] = []) => {
        return new FormState({
            origin: new FieldState(origin),
            groups: new FieldState(groups)
        });
    };



    // Convert FormState structure to RateEntry for API
    private convertToRateEntry = (rateEntryForm: any): RateEntry => {
        const form = rateEntryForm.$;
        const groups = form.groups.value.map((groupForm: any) => {
            const group = groupForm.$;
            return new RateGroup({
                destinations: group.destinations.value,
                rate: new RatePerMile({
                    sprinter: group.rate.value.$.sprinter.value,
                    smallStraight: group.rate.value.$.smallStraight.value,
                    largeStraight: group.rate.value.$.largeStraight.value
                })
            });
        });

        return new RateEntry({
            origin: form.origin.value,
            groups: groups
        });
    };

    // Convert RateEntry from API to FormState structure
    private convertFromRateEntry = (rateEntry: RateEntry) => {
        const groups = rateEntry.groups?.map(group =>
            this.createRateGroupFormState(
                group.destinations || [],
                this.createRatePerMileFormState(
                    group.rate?.sprinter || 0,
                    group.rate?.smallStraight || 0,
                    group.rate?.largeStraight || 0
                )
            )
        ) || [];

        return this.createRateEntryFormState(rateEntry.origin, groups);
    };

    init = async () => {
        await this.wrapInitLoadingApiCall(async () => {
            const [rates, regionDefinitions] = await Promise.all([
                this.companyApiClients.rateSettingsClient.getCurrentRates(),
                this.companyApiClients.rateSettingsClient.getRegionDefinitions()
            ]);

            runInAction(() => {
                // Get all available regions from region definitions
                this.allRegions = regionDefinitions;

                // Create a map of existing rate entries by region
                const existingRateEntries = new Map<Region, RateEntry>();
                rates.forEach(rateEntry => {
                    if (rateEntry.origin) {
                        existingRateEntries.set(rateEntry.origin, rateEntry);
                    }
                });

                // Create FormState entries for all regions, adding empty ones for missing regions
                const rateEntryForms = this.allRegions.map(region => {
                    const existingEntry = existingRateEntries.get(region.id);
                    if (existingEntry) {
                        return this.convertFromRateEntry(existingEntry);
                    } else {
                        // Create empty RateEntry for missing region
                        const emptyRateEntry = new RateEntry({
                            origin: region.id,
                            groups: []
                        });
                        return this.convertFromRateEntry(emptyRateEntry);
                    }
                });

                this.ratesFormState = new FormState(rateEntryForms);
            });
        });
    }

    saveRates = async () => {
        await this.wrapLoadingApiCall(async () => {
            // Convert FormState structure back to RateEntry for API
            const rates = this.ratesFormState.$.map(rateEntryForm => this.convertToRateEntry(rateEntryForm));
            await this.companyApiClients.rateSettingsClient.saveRates(rates);
        });
    }

    addGroup = (originRegion: Region) => {
        runInAction(() => {
            const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
            if (rateEntryForm) {
                const newGroupForm = this.createRateGroupFormState();
                const currentGroups = rateEntryForm.$.groups.value;
                currentGroups.push(newGroupForm);
                rateEntryForm.$.groups.value = [...currentGroups];
            }
        });
    }

    removeGroup = (originRegion: Region, groupIndex: number) => {
        runInAction(() => {
            const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
            if (rateEntryForm) {
                const currentGroups = rateEntryForm.$.groups.value;
                currentGroups.splice(groupIndex, 1);
                rateEntryForm.$.groups.value = [...currentGroups];
            }
        });
    }

    addDestination = (originRegion: Region, groupIndex: number, destination: Region) => {
        runInAction(() => {
            const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
            if (rateEntryForm && rateEntryForm.$.groups.value[groupIndex]) {
                const groupForm = rateEntryForm.$.groups.value[groupIndex];
                const currentDestinations = groupForm.$.destinations.value;

                // Check if this destination is already used in any other group for this origin
                const isDestinationUsed = rateEntryForm.$.groups.value.some((otherGroupForm: any, otherIndex: number) =>
                    otherIndex !== groupIndex &&
                    otherGroupForm.$.destinations.value.includes(destination)
                );

                if (isDestinationUsed) {
                    console.warn(`Destination ${this.getRegionName(destination)} is already assigned to another group for ${this.getRegionName(originRegion)}`);
                    return; // Don't add the destination if it's already used
                }

                if (!currentDestinations.includes(destination)) {
                    currentDestinations.push(destination);
                    groupForm.$.destinations.value = [...currentDestinations];
                }
            }
        });
    }

    removeDestination = (originRegion: Region, groupIndex: number, destination: Region) => {
        runInAction(() => {
            const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
            if (rateEntryForm && rateEntryForm.$.groups.value[groupIndex]) {
                const groupForm = rateEntryForm.$.groups.value[groupIndex];
                const currentDestinations = groupForm.$.destinations.value;
                const index = currentDestinations.indexOf(destination);
                if (index > -1) {
                    currentDestinations.splice(index, 1);
                    groupForm.$.destinations.value = [...currentDestinations];
                }
            }
        });
    }

    updateRate = (originRegion: Region, groupIndex: number, vehicleType: 'sprinter' | 'smallStraight' | 'largeStraight', value: number) => {
        runInAction(() => {
            const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
            if (rateEntryForm && rateEntryForm.$.groups.value[groupIndex]) {
                const groupForm = rateEntryForm.$.groups.value[groupIndex];
                groupForm.$.rate.value.$[vehicleType].value = value;
            }
        });
    }

    getRegionName = (region: Region): string => {
        switch (region) {
            case Region.EastNorthCentral: return 'East North Central';
            case Region.EastSouthCentral: return 'East South Central';
            case Region.MidAtlantic: return 'Mid Atlantic';
            case Region.Mountain: return 'Mountain';
            case Region.NewEngland: return 'New England';
            case Region.Pacific: return 'Pacific';
            case Region.SouthAtlantic: return 'South Atlantic';
            case Region.WestNorthCentral: return 'West North Central';
            case Region.WestSouthCentral: return 'West South Central';
            default: return 'Unknown';
        }
    }

    getAvailableDestinations = (originRegion: Region, groupIndex: number): Region[] => {
        const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
        if (!rateEntryForm || !rateEntryForm.$.groups.value[groupIndex]) {
            return Object.keys(Region)
                .filter(key => isNaN(Number(key)))
                .map(key => Region[key as keyof typeof Region]);
        }

        const currentDestinations = rateEntryForm.$.groups.value[groupIndex].$.destinations.value;

        // Get all destinations used in other groups for this origin
        const usedInOtherGroups = rateEntryForm.$.groups.value
            .filter((_: any, index: number) => index !== groupIndex)
            .flatMap((groupForm: any) => groupForm.$.destinations.value);

        return Object.keys(Region)
            .filter(key => isNaN(Number(key)))
            .map(key => Region[key as keyof typeof Region])
            .filter(r => !currentDestinations.includes(r) && !usedInOtherGroups.includes(r));
    }

    getUsedDestinations = (originRegion: Region): Region[] => {
        const rateEntryForm = this.ratesFormState.$.find((form: any) => form.$.origin.value === originRegion);
        if (!rateEntryForm) {
            return [];
        }

        const usedDestinations: Region[] = [];
        rateEntryForm.$.groups.value.forEach((groupForm: any) => {
            groupForm.$.destinations.value.forEach((dest: Region) => {
                if (!usedDestinations.includes(dest)) {
                    usedDestinations.push(dest);
                }
            });
        });
        return usedDestinations;
    }

    getUnusedDestinations = (originRegion: Region): Region[] => {
        const usedDestinations = this.getUsedDestinations(originRegion);
        let regions = Object.keys(Region)
            .filter(key => isNaN(Number(key)))
            .map(key => Region[key as keyof typeof Region]);
        return regions.filter(r => !usedDestinations.includes(r));
    }
}
